import { useCallback } from 'react';
import { toast } from '@/components/ui/use-toast';
import { VaultFilePickerDialog, type VaultPickerResult } from '@/components/VaultFilePickerDialog';

export interface FiasImagePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImageSelected: (file: { fileId: string; fileName: string; dataUrl: string }) => void;
}

export function FiasImagePickerDialog({
  open,
  onOpenChange,
  onImageSelected,
}: FiasImagePickerDialogProps) {
  const handlePicked = useCallback(
    async (picked: VaultPickerResult) => {
      try {
        const response = await fetch(picked.downloadUrl);
        if (!response.ok) throw new Error(`Download failed: ${response.status}`);
        const blob = await response.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        onImageSelected({
          fileId: picked.documentId,
          fileName: picked.fileName,
          dataUrl,
        });
      } catch (err) {
        toast({
          title: 'Failed to load image',
          description: err instanceof Error ? err.message : 'An error occurred',
          variant: 'destructive',
        });
      }
    },
    [onImageSelected],
  );

  return (
    <VaultFilePickerDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Open Fias image"
      description="Pick an image from your Fias storage to load into the editor."
      mimeFilter="image"
      uploadAccept="image/*"
      confirmLabel="Open"
      onFileSelected={handlePicked}
    />
  );
}
